import React, { useEffect, useRef, useState, useCallback } from "react";
import { getApiUrl } from "../../api";
import { useNavigate } from "react-router-dom";

interface Program {
  program_id: number;
  program_name: string;
  thumbnail_url: string | null;
}

//주석추가
const CARD_WIDTH_DESKTOP = 300;
const CARD_GAP = 24;
const TRANSITION_MS = 500;


export default function ProgramSlider() {
  const [programs, setPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [trackIndex, setTrackIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const trackRef = useRef<HTMLDivElement>(null);
  const autoPlayRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setScreenWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("access_token");
        const res = await fetch(getApiUrl("/marubit/api/program/list?is_visible=1&limit=10"), {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) return;
        const data = await res.json();
        const list: Program[] = (data.programs ?? []).filter((p: Program) => p.thumbnail_url);
        setPrograms(list);
        setTrackIndex(list.length);
      } catch (e) {
        console.error("슬라이더 로딩 실패:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const mobileCardWidth = (screenWidth - 48 - CARD_GAP) / 2;
  const cardWidth = isMobile ? mobileCardWidth : CARD_WIDTH_DESKTOP;
  const windowWidth = isMobile
    ? screenWidth - 48
    : CARD_WIDTH_DESKTOP * 3 + CARD_GAP * 2;
  const slideUnit = cardWidth + CARD_GAP;

  const clonedTrack = programs.length > 0
    ? [...programs, ...programs, ...programs]
    : [];

  const slideTo = useCallback((dir: 1 | -1) => {
    if (isTransitioning || programs.length === 0) return;
    setIsTransitioning(true);
    setTrackIndex(prev => prev + dir);
    setCurrentIndex(prev => (prev + dir + programs.length) % programs.length);
    setTimeout(() => {
      setIsTransitioning(false);
      setTrackIndex(prev => {
        const n = programs.length;
        if (prev >= n * 2) return prev - n;
        if (prev < n) return prev + n;
        return prev;
      });
    }, TRANSITION_MS);
  }, [isTransitioning, programs.length]);

  useEffect(() => {
    if (programs.length === 0) return;
    autoPlayRef.current = setInterval(() => slideTo(1), 3000);
    return () => { if (autoPlayRef.current) clearInterval(autoPlayRef.current); };
  }, [programs, slideTo]);

  if (loading) return (
    <div className="h-[420px] flex items-center justify-center text-gray-300">
      불러오는 중...
    </div>
  );

  if (programs.length === 0) return (
    <div className="h-[420px] flex items-center justify-center text-gray-300">
      등록된 프로그램이 없습니다.
    </div>
  );

  const trackOffset = -(trackIndex - 1) * slideUnit;

  return (
    <div className="py-[30px] pb-10 bg-[#f5f2f0]">

      {/* 섹션 타이틀 */}
      <div className="px-6 pb-6 max-w-[1200px] mx-auto">
        <h2 className="text-[20px] font-bold text-[#5a4035] border-l-4 border-[#887267] pl-3 m-0">
          프로그램 소개
        </h2>
      </div>

      <div className="relative flex items-center justify-center">

        {/* 이전 버튼 */}
        <button
          onClick={() => slideTo(-1)}
          className="absolute z-10 bg-white/90 border-none rounded-full w-11 h-11 cursor-pointer shadow-[0_2px_10px_rgba(0,0,0,0.15)] flex items-center justify-center"
          style={{ left: isMobile ? "4px" : `calc(50% - ${windowWidth / 2 + 56}px)` }}
        >
          <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="#555" strokeWidth={2.5}>
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>

        {/* 슬라이드 윈도우 */}
        <div style={{ width: `${windowWidth}px` }} className="overflow-hidden">
          <div
            ref={trackRef}
            className="flex"
            style={{
              gap: `${CARD_GAP}px`,
              transform: `translateX(${trackOffset}px)`,
              transition: isTransitioning ? `transform ${TRANSITION_MS}ms cubic-bezier(0.4, 0, 0.2, 1)` : "none",
            }}
          >
            {clonedTrack.map((program, idx) => {
              const relIdx = idx - trackIndex;
              const isCenter = relIdx === 0;
              const isAdjacent = Math.abs(relIdx) === 1;

              const opacity = isMobile
                ? (Math.abs(relIdx) <= 1 ? 1 : 0)
                : (isCenter ? 1 : isAdjacent ? 0.6 : 0);
              const transform = isMobile
                ? "scale(1)"
                : (isCenter ? "scale(1)" : isAdjacent ? "scale(0.91)" : "scale(0.85)");
              const boxShadow = isMobile
                ? "0 2px 12px rgba(0,0,0,0.08)"
                : (isCenter ? "0 10px 36px rgba(124,58,237,0.18)" : "0 2px 12px rgba(0,0,0,0.08)");

              return (
                <div
                  key={`${program.program_id}-${idx}`}
                  onClick={() => {
                    if (relIdx === 0) {
                      navigate(`/program/${program.program_id}`);
                    } else if (!isMobile) {
                      if (relIdx === 1) slideTo(1);
                      else if (relIdx === -1) slideTo(-1);
                    }
                  }}
                  className="shrink-0 rounded-[14px] overflow-hidden bg-white"
                  style={{
                    width: `${cardWidth}px`,
                    boxShadow,
                    opacity,
                    transform,
                    transition: `opacity ${TRANSITION_MS}ms ease, transform ${TRANSITION_MS}ms ease, box-shadow ${TRANSITION_MS}ms ease`,
                    cursor: relIdx === 0 ? "pointer" : "default",
                    pointerEvents: Math.abs(relIdx) <= 1 ? "auto" : "none",
                  }}
                >
                  <div className="w-full relative bg-gray-200" style={{ paddingTop: "130%" }}>
                    <img
                      src={program.thumbnail_url!}
                      alt={program.program_name}
                      className="absolute inset-0 w-full h-full object-cover"
                      onError={e => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                    />
                  </div>
                  <div className="px-4 py-3">
                    <p className="m-0 text-[14px] font-semibold text-[#222] overflow-hidden text-ellipsis whitespace-nowrap">
                      {program.program_name}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 다음 버튼 */}
        <button
          onClick={() => slideTo(1)}
          className="absolute z-10 bg-white/90 border-none rounded-full w-11 h-11 cursor-pointer shadow-[0_2px_10px_rgba(0,0,0,0.15)] flex items-center justify-center"
          style={{ right: isMobile ? "4px" : `calc(50% - ${windowWidth / 2 + 56}px)` }}
        >
          <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="#555" strokeWidth={2.5}>
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>

      {/* 도트 인디케이터 */}
      <div className="flex justify-center gap-2 mt-6">
        {programs.map((_, idx) => (
          <button
            key={idx}
            onClick={() => {
              if (!isTransitioning && idx !== currentIndex) {
                slideTo(idx > currentIndex ? 1 : -1);
              }
            }}
            className="h-2 rounded border-none cursor-pointer p-0 transition-all duration-300"
            style={{
              width: idx === currentIndex ? "24px" : "8px",
              backgroundColor: idx === currentIndex ? "#887267" : "#ddd",
            }}
          />
        ))}
      </div>
    </div>
  );
}